/**
 * ConfirmDialog — branded replacement for window.confirm().
 *
 * Use anywhere a destructive or bulk action needs operator buy-in.
 * Matches the rest of the owner-console chrome (border-rose tone for
 * danger, border-default for primary), focus-traps the confirm button,
 * closes on Esc, dismisses on backdrop click (unless `busy`).
 *
 * Usage:
 *   const [confirmOpen, setConfirmOpen] = useState(false);
 *   ...
 *   <ConfirmDialog
 *     open={confirmOpen}
 *     title="Skip 12 review items?"
 *     body="Skipped items stop showing in the queue but stay in the DB."
 *     confirmLabel="Skip 12"
 *     tone="danger"
 *     busy={busy}
 *     onConfirm={async () => { await doSkip(); setConfirmOpen(false); }}
 *     onCancel={() => setConfirmOpen(false)}
 *   />
 */
import { useEffect, useRef } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";

export default function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  tone = "primary",
  busy = false,
  onConfirm,
  onCancel,
}) {
  const confirmRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    function onKey(e) {
      if (e.key === "Escape" && !busy) onCancel?.();
    }
    document.addEventListener("keydown", onKey);
    // Autofocus the confirm button so Enter triggers the primary action.
    confirmRef.current?.focus();
    return () => document.removeEventListener("keydown", onKey);
  }, [open, busy, onCancel]);

  if (!open) return null;

  const isDanger = tone === "danger";
  const borderTone = isDanger ? "border-rose-500/40" : "border-border-default";
  const titleTone = isDanger ? "text-rose-300" : "text-text-primary";
  const confirmCls = isDanger
    ? "bg-rose-600 hover:bg-rose-500 text-white"
    : "bg-button-primary hover:opacity-90 text-white";

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onCancel?.();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`bg-bg-primary text-text-primary border ${borderTone} rounded-lg w-full max-w-md overflow-hidden`}
      >
        <div className={`px-5 py-3 border-b border-border-default flex items-center gap-2 font-semibold ${titleTone}`}>
          {isDanger && <AlertTriangle className="w-4 h-4" aria-hidden="true" />}
          {title}
        </div>
        {body && (
          <div className="px-5 py-4 text-sm text-text-secondary leading-relaxed">
            {body}
          </div>
        )}
        <div className="px-5 py-3 border-t border-border-default flex items-center justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={busy}
            className="px-3 py-1.5 rounded-md border border-border-default hover:bg-bg-secondary text-sm disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            ref={confirmRef}
            onClick={onConfirm}
            disabled={busy}
            className={`px-3 py-1.5 rounded-md text-sm font-semibold flex items-center gap-1.5 disabled:opacity-50 ${confirmCls}`}
          >
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />}
            {busy ? "Working…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
